import { Box, Collapse } from "@mui/material";
import { DotLegalDialog, DotLegalLink, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import RiskScoringForm from "../../riskScoring/RiskScoringForm";
import RiskParticipantsPicker from "../../riskScoring/RiskParticipantsPicker";
import LinkedControlsSection from "../../riskControls/LinkedControlsSection";
import { RiskScenarioRow } from "../RiskAssessmentsOverview.types";
import { useAssessDialog } from "./AssessDialog.hooks";
import { useAssessDialogStyles } from "./AssessDialog.styles";
import { CurrentAssessmentCard } from "./CurrentAssessmentCard";

export interface IAssessDialog {
    row: RiskScenarioRow;
    riskAreaId: string;
    onSaved: () => void;
    onClose: () => void;
}

// A single dialog stays mounted across loading → ready, so the modal shell never remounts (no flicker). A scored
// assessment opens on the gate (reference card + Reconfirm/Reassess); an unscored one opens straight on the empty
// scoring form. The form is never prefilled — previous values reach it only through the carry-forward links.
export function AssessDialog(props: IAssessDialog) {
    const styles = useAssessDialogStyles();
    const { translateString } = useTranslation();
    const {
        isLoading,
        scoringForm,
        stage,
        currentAssessment,
        startReconfirm,
        startReassess,
        backToGate,
        reviewNote,
        setReviewNote,
        reconfirm,
        reconfirmInProgress,
        keepPreviousConsequence,
        keepPreviousProbability,
        keepPreviousParticipants,
        validateAndSave,
        inProgress,
        showLinkedControls,
        availableControls,
        linkedControlIds,
        onControlsChange,
    } = useAssessDialog(props);

    // The editable section belongs to the stages that author an assessment (an unscored scenario, or Reassess),
    // and its edits are staged until Save commits them with the scores. On the gate the current assessment's
    // coverage is shown read-only inside the reference card, so coverage is only rewritten as part of a
    // deliberate reassessment.
    const linkedControlsSection = showLinkedControls ? (
        <Box>
            <LinkedControlsSection linkedControlIds={linkedControlIds} availableControls={availableControls} onChange={onControlsChange} />
        </Box>
    ) : undefined;

    const carryForwardLink = (onClick: () => void, textKey = "keepPreviousRating") =>
        currentAssessment != null ? (
            <Box sx={styles.carryForward}>
                <DotLegalLink onClick={onClick} linkColor="primary" leftAlign size="small">
                    {translateString(textKey)}
                </DotLegalLink>
            </Box>
        ) : undefined;

    const isGate = stage === "gate";
    const canReturnToGate = !isGate && currentAssessment != null;

    // The footer differs per stage: the gate offers the Reconfirm/Reassess choice (no commit), while the
    // reconfirm/reassess stages each turn the OK button into the matching Save and offer a secondary Go back
    // to the gate — only when there is a gate to return to (an already-scored scenario).
    const goBackButton = canReturnToGate
        ? { secondaryButtonText: translateString("goBack"), onSecondaryButtonClick: backToGate, secondaryButtonStyle: "secondary" as const }
        : {};

    const footer = {
        gate: {
            buttonOkText: translateString("reconfirm"),
            onOkClick: startReconfirm,
            secondaryButtonText: translateString("reassess"),
            onSecondaryButtonClick: startReassess,
            secondaryButtonStyle: "primary" as const,
        },
        reconfirm: { buttonOkText: translateString("save"), onOkClick: reconfirm, ...goBackButton },
        reassess: { buttonOkText: translateString("save"), onOkClick: validateAndSave, ...goBackButton },
    }[stage];

    return (
        <DotLegalDialog
            open
            size="xl"
            isLoading={isLoading || scoringForm.riskArea === undefined}
            inProgress={stage === "reconfirm" ? reconfirmInProgress : inProgress}
            header={
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {translateString("assess")}
                    <Box sx={styles.dialogSubtitle}>{props.row.name}</Box>
                </Box>
            }
            onDialogClose={props.onClose}
            {...footer}
        >
            {!isLoading && scoringForm.riskArea !== undefined && (
                <Box sx={styles.stage}>
                    {currentAssessment != null && <CurrentAssessmentCard model={currentAssessment} />}

                    {/* Two independent collapses, one per stage: a single collapse would swap its child to the
                        other stage's content the moment stage becomes "gate", flashing it mid-animation. */}
                    <Collapse in={stage === "reconfirm"} unmountOnExit>
                        <Box sx={styles.collapseContent}>
                            <DotLegalTextField
                                debounce={false}
                                noMargin
                                multiline
                                rows={3}
                                label={translateString("reviewNote")}
                                value={reviewNote}
                                onChange={setReviewNote}
                            />
                            <Box sx={{ mt: 1.5 }}>
                                <RiskParticipantsPicker form={scoringForm} footer={carryForwardLink(keepPreviousParticipants, "keepPreviousParticipants")} />
                            </Box>
                        </Box>
                    </Collapse>

                    <Collapse in={stage === "reassess"} unmountOnExit>
                        <Box sx={styles.collapseContent}>
                            <RiskScoringForm
                                form={scoringForm}
                                consequenceFooter={carryForwardLink(keepPreviousConsequence)}
                                probabilityFooter={carryForwardLink(keepPreviousProbability)}
                                participantsFooter={carryForwardLink(keepPreviousParticipants, "keepPreviousParticipants")}
                            />
                        </Box>
                    </Collapse>

                    {/* Related controls is its own full-width section below the stage content, rather than
                        tucked inside the scoring form's probability column. */}
                    {(currentAssessment == null || stage === "reassess") && linkedControlsSection}
                </Box>
            )}
        </DotLegalDialog>
    );
}
